import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { jsPDF } from 'jspdf';
import Tesseract from 'tesseract.js';
import './BulkUploadModal.css';
import { 
  extractTextFromPDF, 
  extractSerialFromPDFText, 
  getPDFMetadata, 
  fileToBase64,
  calculateHash 
} from './utils/pdfExtractor';
import { 
  extractSerialFromFilename, 
  createEquipmentIndex, 
  findMatchingEquipment,
  searchEquipment,
  groupEquipment,
  getUniqueEquipmentNames 
} from './utils/serialMatcher';
import { 
  processZipFile, 
  isValidZipFile, 
  getZipInfo 
} from './utils/zipProcessor';
import { logUpload } from './auditLog';

// Equipment Autocomplete Component
const EquipmentAutocomplete = ({ assets, selectedEquipment, onSelect }) => {
  const [inputValue, setInputValue] = useState(selectedEquipment || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Get unique equipment names from assets
  const equipmentNames = useMemo(() => {
    if (!assets || assets.length === 0) return [];
    const names = new Set();
    assets.forEach(asset => {
      if (asset.equipmentName) {
        names.add(String(asset.equipmentName).trim());
      }
    });
    return Array.from(names).sort();
  }, [assets]);

  // Update input value when selectedEquipment changes externally
  useEffect(() => {
    setInputValue(selectedEquipment || '');
  }, [selectedEquipment]);

  // Filter suggestions based on input
  useEffect(() => {
    if (!inputValue.trim()) {
      setSuggestions([]);
      return;
    }

    const searchTerm = String(inputValue).toLowerCase().trim();
    const filtered = equipmentNames.filter(name => 
      String(name).toLowerCase().includes(searchTerm)
    );
    setSuggestions(filtered.slice(0, 10)); // Limit to 10 suggestions
    setHighlightedIndex(-1);
  }, [inputValue, equipmentNames]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(true);

    const normalizedValue = String(value || '').trim().toLowerCase();
    const exactMatch = equipmentNames.find(name => String(name).trim().toLowerCase() === normalizedValue);
    if (exactMatch) {
      onSelect(exactMatch);
    }
    
    // If input is cleared, clear selection
    if (!value.trim()) {
      onSelect('');
    }
  };

  const handleSuggestionClick = (name) => {
    setInputValue(name);
    setShowSuggestions(false);
    onSelect(name);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      const normalizedValue = String(inputValue || '').trim().toLowerCase();
      const exactMatch = equipmentNames.find(name => String(name).trim().toLowerCase() === normalizedValue);
      if (exactMatch) {
        onSelect(exactMatch);
      }
    }, 120);
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setShowSuggestions(true);
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        } else if (suggestions.length === 1) {
          handleSuggestionClick(suggestions[0]);
        } else if (inputValue.trim() && equipmentNames.includes(inputValue.trim())) {
          handleSuggestionClick(inputValue.trim());
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (inputValue.trim()) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="form-section equipment-autocomplete-wrapper" ref={wrapperRef}>
      <label>Select Equipment Name:</label>
      <div className="autocomplete-container">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Type equipment name..."
          className="equipment-autocomplete-input"
          autoComplete="off"
        />
        {inputValue && (
          <button 
            className="clear-input-btn"
            onClick={() => {
              setInputValue('');
              onSelect('');
              inputRef.current?.focus();
            }}
            type="button"
          >
            ×
          </button>
        )}
        {showSuggestions && suggestions.length > 0 && (
          <ul className="autocomplete-suggestions">
            {suggestions.map((name, index) => (
              <li
                key={name}
                className={`suggestion-item ${index === highlightedIndex ? 'highlighted' : ''}`}
                onClick={() => handleSuggestionClick(name)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSuggestionClick(name);
                }}
                onTouchStart={() => handleSuggestionClick(name)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {name}
              </li>
            ))}
          </ul>
        )}
        {showSuggestions && inputValue.trim() && suggestions.length === 0 && (
          <div className="autocomplete-no-results">
            No matching equipment found
          </div>
        )}
      </div>
      <small className="help-text">
        Type to search equipment names. Example: "Syringe Pump"
      </small>
    </div>
  );
};

const BulkUploadModal = ({
  isOpen,
  onClose,
  assets,
  onBulkUploadComplete
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('bulk'); // 'bulk', 'camera', 'grouping'
  
  // Upload queue and processing state
  const [uploadQueue, setUploadQueue] = useState([]);
  const [processingStatus, setProcessingStatus] = useState({
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    currentFile: null,
    isProcessing: false
  });
  
  // Manual mapping state
  const [manualMappings, setManualMappings] = useState([]);
  const [equipmentIndex, setEquipmentIndex] = useState(null);
  
  // Camera state
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImages, setCapturedImages] = useState([]);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraFileInputRef = useRef(null);
  
  // Smart grouping state
  const [groupCriteria, setGroupCriteria] = useState('equipmentName');
  const [selectedEquipment, setSelectedEquipment] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupReportFile, setGroupReportFile] = useState(null);
  const [groupReportPreview, setGroupReportPreview] = useState('');
  const [groupReportType, setGroupReportType] = useState('pm');
  
  // Report type for bulk upload
  const [reportType, setReportType] = useState('pm');
  
  // Results state
  const [uploadResults, setUploadResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);

  const normalizeId = useCallback((value) => String(value ?? '').trim(), []);

  // Initialize equipment index when modal opens
  useEffect(() => {
    if (isOpen && assets) {
      setEquipmentIndex(createEquipmentIndex(assets));
    }
  }, [isOpen, assets]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  useEffect(() => {
    if (!cameraStream || !videoRef.current) return;

    const videoElement = videoRef.current;
    videoElement.srcObject = cameraStream;
    videoElement.play().catch(() => {});
  }, [cameraStream, isCameraActive]);

  // ===== BULK UPLOAD HANDLERS =====
  
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    
    // Process dropped files
    await handleFileSelect({ target: { files } });
  }, []);

  const handleFileSelect = useCallback(async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const newQueueItems = [];
    
    for (const file of files) {
      if (isValidZipFile(file)) {
        // Process ZIP file
        try {
          const zipInfo = await getZipInfo(file);
          if (zipInfo.pdfCount > 0) {
            newQueueItems.push({
              id: Date.now() + Math.random(),
              file,
              name: file.name,
              type: 'zip',
              pdfCount: zipInfo.pdfCount,
              status: 'pending'
            });
          }
        } catch (error) {
          console.error('Error reading ZIP:', error);
        }
      } else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        // Process PDF file
        newQueueItems.push({
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          type: 'pdf',
          status: 'pending'
        });
      } else if (file.type.startsWith('image/') || 
                 file.name.toLowerCase().match(/\.(jpg|jpeg|png)$/)) {
        // Process image files (JPG, JPEG, PNG)
        newQueueItems.push({
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          type: 'image',
          status: 'pending'
        });
      }
    }
    
    setUploadQueue(prev => [...prev, ...newQueueItems]);
  }, []);

  const removeFromQueue = useCallback((id) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  }, []);

  const clearQueue = useCallback(() => {
    setUploadQueue([]);
    setManualMappings([]);
    setUploadResults([]);
    setShowResults(false);
  }, []);

  const processUploadQueue = useCallback(async () => {
    if (uploadQueue.length === 0 || !equipmentIndex) return;
    
    setProcessingStatus({
      total: uploadQueue.length,
      processed: 0,
      successful: 0,
      failed: 0,
      currentFile: null,
      isProcessing: true
    });
    
    const results = [];
    const unmatched = [];
    
    for (let i = 0; i < uploadQueue.length; i++) {
      const item = uploadQueue[i];
      setProcessingStatus(prev => ({ ...prev, currentFile: item.name }));
      
      try {
        let filesToProcess = [];
        
        if (item.type === 'zip') {
          // Extract PDFs from ZIP
          const extracted = await processZipFile(item.file);
          filesToProcess = extracted.filter(f => !f.error).map(f => ({
            file: f.file,
            name: f.name,
            source: 'zip'
          }));
        } else {
          filesToProcess = [{
            file: item.file,
            name: item.name,
            source: 'direct'
          }];
        }
        
        // Process each file
        for (const fileItem of filesToProcess) {
          const result = await processSingleFile(fileItem, equipmentIndex);
          results.push(result);
          
          if (!result.matchedEquipment) {
            unmatched.push({
              ...result,
              queueId: item.id,
              resultIndex: results.length - 1
            });
          }
        }
        
        setProcessingStatus(prev => ({
          ...prev,
          processed: i + 1,
          successful: prev.successful + filesToProcess.length
        }));
        
      } catch (error) {
        console.error('Error processing item:', error);
        setProcessingStatus(prev => ({
          ...prev,
          processed: i + 1,
          failed: prev.failed + 1
        }));
        results.push({
          name: item.name,
          error: error.message,
          status: 'failed'
        });
      }
    }
    
    setUploadResults(results);
    setManualMappings(unmatched);
    setProcessingStatus(prev => ({ ...prev, isProcessing: false, currentFile: null }));
    setShowResults(true);
    
    // Log bulk upload
    logUpload('bulk_upload', {
      totalFiles: uploadQueue.length,
      successful: results.filter(r => r.status === 'success').length,
      failed: results.filter(r => r.status === 'failed').length,
      unmatched: unmatched.length
    });
  }, [uploadQueue, equipmentIndex, reportType]);

  const processSingleFile = async (fileItem, index) => {
    const { file, name } = fileItem;
    
    try {
      // Convert to base64
      const base64Data = await fileToBase64(file);
      
      // Calculate hash for duplicate detection
      const fileHash = calculateHash(base64Data);
      
      // Try to extract serial from filename (primary method)
      let serialInfo = extractSerialFromFilename(name);
      
      // If not found in filename, try PDF content (for PDF files only)
      const isPDF = file.type === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
      if (!serialInfo && isPDF) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const pdfText = await extractTextFromPDF(arrayBuffer);
          serialInfo = extractSerialFromPDFText(pdfText);
        } catch (e) {
          console.log('Could not extract text from PDF');
        }
      }
      
      // Find matching equipment
      let matchedEquipment = null;
      let matchConfidence = 'none';
      let matchMethod = 'none';
      
      if (serialInfo) {
        const match = findMatchingEquipment(serialInfo.value, index, assets);
        matchedEquipment = match.match;
        matchConfidence = match.confidence;
        matchMethod = match.method;
      }
      
      // Get file metadata
      let pageCount = 1;
      if (isPDF) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const metadata = await getPDFMetadata(arrayBuffer);
          pageCount = metadata.pageCount;
        } catch (e) {
          console.log('Could not get PDF metadata');
        }
      }
      
      return {
        name,
        file,
        base64Data,
        fileHash,
        serialInfo,
        matchedEquipment,
        matchConfidence,
        matchMethod,
        pageCount,
        status: matchedEquipment ? 'success' : 'unmatched',
        reportType
      };
      
    } catch (error) {
      return {
        name,
        error: error.message,
        status: 'failed'
      };
    }
  };

  const handleManualMapping = useCallback((resultIndex, equipmentId) => {
    const targetId = normalizeId(equipmentId);
    const equipment = assets.find(a => {
      const idMatch = normalizeId(a.id) === targetId;
      const assetIdMatch = normalizeId(a.assetId) === targetId;
      return idMatch || assetIdMatch;
    });

    if (!equipment) {
      return;
    }
    
    setUploadResults(prev => {
      const updated = [...prev];
      updated[resultIndex] = {
        ...updated[resultIndex],
        matchedEquipment: equipment,
        matchConfidence: 'manual',
        status: 'success'
      };
      return updated;
    });
    
    setManualMappings(prev => prev.filter(mapping => mapping.resultIndex !== resultIndex));
  }, [assets, normalizeId]);

  const confirmBulkUpload = useCallback(() => {
    const successfulUploads = uploadResults.filter(r => r.status === 'success' && r.matchedEquipment);
    
    if (successfulUploads.length === 0) {
      alert('No successful uploads to confirm');
      return;
    }
    
    if (!window.confirm(`Confirm upload of ${successfulUploads.length} reports?`)) {
      return;
    }
    
    // Group uploads by equipment for batch processing
    const uploadsByEquipment = {};
    successfulUploads.forEach(upload => {
      const equipId = upload.matchedEquipment.id;
      if (!uploadsByEquipment[equipId]) {
        uploadsByEquipment[equipId] = [];
      }
      uploadsByEquipment[equipId].push(upload);
    });
    
    // Execute uploads
    let successCount = 0;
    let failCount = 0;
    
    Object.entries(uploadsByEquipment).forEach(([equipId, uploads]) => {
      uploads.forEach(upload => {
        try {
          const uploadAssetId = upload.matchedEquipment?.id ?? upload.matchedEquipment?.assetId;
          onBulkUploadComplete({
            assetId: uploadAssetId,
            reportData: upload.base64Data,
            reportType: upload.reportType,
            fileName: upload.name,
            pageCount: upload.pageCount,
            source: 'bulk_upload'
          });
          successCount++;
        } catch (err) {
          failCount++;
          console.error('Upload failed for asset', equipId, err);
        }
      });
    });
    
    // Show result message
    if (failCount === 0) {
      alert(`✅ Upload Successful!\n${successCount} report(s) assigned to equipment successfully.`);
    } else if (successCount === 0) {
      alert(`❌ Upload Failed!\nAll ${failCount} report(s) failed to save. Please try again.`);
    } else {
      alert(`⚠️ Partial Upload:\n✅ ${successCount} report(s) uploaded successfully.\n❌ ${failCount} report(s) failed.`);
    }
    
    // Reset and close
    clearQueue();
    onClose();
  }, [uploadResults, onBulkUploadComplete, clearQueue, onClose]);

  // ===== CAMERA HANDLERS =====

  const handleMobileCameraCapture = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    files.forEach((file) => {
      if (!file.type.startsWith('image/')) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result;
        if (typeof imageData === 'string') {
          setCapturedImages(prev => [...prev, imageData]);
        }
      };
      reader.readAsDataURL(file);
    });

    event.target.value = '';
  }, []);
  
  const startCamera = useCallback(async () => {
    const isSecureCameraContext = window.isSecureContext || ['localhost', '127.0.0.1'].includes(window.location.hostname);

    if (!isSecureCameraContext) {
      alert('Direct camera preview requires HTTPS on mobile. Opening camera file capture instead.');
      cameraFileInputRef.current?.click();
      return;
    }

    if (!navigator?.mediaDevices?.getUserMedia) {
      alert('Live camera preview is not supported on this device/browser. Opening camera file capture instead.');
      cameraFileInputRef.current?.click();
      return;
    }

    try {
      const preferredConstraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(preferredConstraints);
      } catch (_primaryError) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }

      setCameraStream(stream);
      setIsCameraActive(true);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    } catch (error) {
      console.error('Camera error:', error);
      alert('Could not access live camera. Opening camera file capture instead.');
      cameraFileInputRef.current?.click();
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
  }, [cameraStream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !isCameraActive) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current || document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImages(prev => [...prev, imageData]);
  }, [isCameraActive]);

  const removeCapturedImage = useCallback((index) => {
    setCapturedImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  const processCameraImages = useCallback(async () => {
    if (capturedImages.length === 0) return;
    
    setProcessingStatus(prev => ({ ...prev, isProcessing: true }));
    
    try {
      // Convert images to PDF
      const doc = new jsPDF();
      capturedImages.forEach((imgData, index) => {
        if (index > 0) doc.addPage();
        const imgWidth = 190;
        const imgHeight = (canvasRef.current?.height / canvasRef.current?.width) * imgWidth || 100;
        doc.addImage(imgData, 'JPEG', 10, 10, imgWidth, imgHeight);
      });
      
      const pdfData = doc.output('datauristring');
      
      // Perform OCR on first image to extract serial
      setOcrProgress(0);
      let serialInfo = null;
      
      try {
        const result = await Tesseract.recognize(
          capturedImages[0],
          'eng',
          { 
            logger: m => {
              if (m.status === 'recognizing text') {
                setOcrProgress(Math.round(m.progress * 100));
              }
            }
          }
        );
        
        serialInfo = extractSerialFromPDFText(result.data.text);
      } catch (ocrError) {
        console.error('OCR error:', ocrError);
      }
      
      // Find matching equipment
      let matchedEquipment = null;
      if (serialInfo && equipmentIndex) {
        const match = findMatchingEquipment(serialInfo.value, equipmentIndex, assets);
        matchedEquipment = match.match;
      }
      
      // Add to upload queue
      const newUpload = {
        id: Date.now(),
        name: `Camera_Capture_${new Date().toISOString().slice(0,10)}.pdf`,
        base64Data: pdfData,
        serialInfo,
        matchedEquipment,
        pageCount: capturedImages.length,
        status: matchedEquipment ? 'success' : 'unmatched',
        reportType,
        source: 'camera'
      };
      
      setUploadResults([newUpload]);
      
      if (!matchedEquipment) {
        setManualMappings([newUpload]);
      }
      
      setShowResults(true);
      
      // Stop camera
      stopCamera();
      setCapturedImages([]);
      
    } catch (error) {
      console.error('Error processing camera images:', error);
      alert('Error converting images to PDF');
    } finally {
      setProcessingStatus(prev => ({ ...prev, isProcessing: false }));
      setOcrProgress(0);
    }
  }, [capturedImages, equipmentIndex, assets, reportType, stopCamera]);

  // ===== SMART GROUPING HANDLERS =====
  
  const groupedEquipment = useMemo(() => {
    if (!assets) return {};
    return groupEquipment(assets, groupCriteria);
  }, [assets, groupCriteria]);

  const toggleGroupSelection = useCallback((groupKey) => {
    setSelectedGroups(prev => {
      if (prev.includes(groupKey)) {
        return prev.filter(g => g !== groupKey);
      }
      return [...prev, groupKey];
    });
  }, []);

  const handleGroupFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setGroupReportFile(file);
      setGroupReportPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  }, []);

  const executeGroupUpload = useCallback(() => {
    if (!selectedEquipment) {
      alert('Please select an equipment name');
      return;
    }
    
    if (!groupReportPreview) {
      alert('Please upload a report file');
      return;
    }
    
    // Get all equipment with the selected name
    const targetEquipment = groupedEquipment[selectedEquipment] || [];
    
    if (targetEquipment.length === 0) {
      alert('No equipment found for selected name');
      return;
    }
    
    if (!window.confirm(`Assign report to ${targetEquipment.length} equipment items?`)) {
      return;
    }
    
    // Upload to all selected equipment
    let groupSuccessCount = 0;
    let groupFailCount = 0;
    
    targetEquipment.forEach(equipment => {
      try {
        onBulkUploadComplete({
          assetId: equipment.id ?? equipment.assetId,
          reportData: groupReportPreview,
          reportType: groupReportType,
          fileName: groupReportFile?.name || 'Group_Report.pdf',
          pageCount: 1,
          source: 'group_upload',
          groupInfo: {
            criteria: 'equipmentName',
            equipmentName: selectedEquipment
          }
        });
        groupSuccessCount++;
      } catch (err) {
        groupFailCount++;
        console.error('Group upload failed for equipment', equipment.id, err);
      }
    });
    
    // Show result message
    if (groupFailCount === 0) {
      alert(`✅ Upload Successful!\nReport assigned to ${groupSuccessCount} ${selectedEquipment} equipment(s) successfully.`);
    } else if (groupSuccessCount === 0) {
      alert(`❌ Upload Failed!\nFailed to assign report to ${groupFailCount} equipment(s). Please try again.`);
    } else {
      alert(`⚠️ Partial Upload:\n✅ ${groupSuccessCount} uploaded successfully.\n❌ ${groupFailCount} failed.`);
    }
    
    // Reset
    setSelectedGroups([]);
    setSelectedEquipment('');
    setGroupReportFile(null);
    setGroupReportPreview('');
    onClose();
  }, [selectedEquipment, groupedEquipment, groupReportPreview, groupReportFile, groupReportType, onBulkUploadComplete, onClose]);

  // ===== RENDER HELPERS =====
  
  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay bulk-upload-overlay">
      <div className="modal-content bulk-upload-modal">
        <div className="modal-header">
          <h3>Bulk Upload Reports</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        
        {/* Tabs */}
        <div className="bulk-upload-tabs">
          <button 
            className={`tab-btn ${activeTab === 'bulk' ? 'active' : ''}`}
            onClick={() => {
              if (isCameraActive) stopCamera();
              setActiveTab('bulk');
            }}
          >
            Bulk Upload
          </button>
          <button 
            className={`tab-btn ${activeTab === 'camera' ? 'active' : ''}`}
            onClick={() => setActiveTab('camera')}
          >
            Camera Capture
          </button>
          <button 
            className={`tab-btn ${activeTab === 'grouping' ? 'active' : ''}`}
            onClick={() => {
              if (isCameraActive) stopCamera();
              setActiveTab('grouping');
            }}
          >
            Smart Grouping
          </button>
        </div>
        
        <div className="modal-body">
          {/* BULK PDF UPLOAD TAB */}
          {activeTab === 'bulk' && (
            <div className="bulk-upload-tab">
              {!showResults ? (
                <>
                  {/* Report Type Selection */}
                  <div className="form-section">
                    <label>Report Type:</label>
                    <div className="report-type-options">
                      <label className={`radio-label ${reportType === 'pm' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          value="pm"
                          checked={reportType === 'pm'}
                          onChange={(e) => setReportType(e.target.value)}
                        />
                        <span>PM Report</span>
                      </label>
                      <label className={`radio-label ${reportType === 'cal' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          value="cal"
                          checked={reportType === 'cal'}
                          onChange={(e) => setReportType(e.target.value)}
                        />
                        <span>Calibration Report</span>
                      </label>
                      <label className={`radio-label ${reportType === 'both' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          value="both"
                          checked={reportType === 'both'}
                          onChange={(e) => setReportType(e.target.value)}
                        />
                        <span>Both (PM + Calibration)</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Filename Matching Info */}
                  <div className="filename-matching-info">
                    <div className="info-icon">💡</div>
                    <div className="info-text">
                      <strong>Auto-Match by Filename:</strong> Name your files as <code>SerialNumber.pdf</code> or <code>AssetID.pdf</code> (e.g., "SN12345.pdf", "ECG001.pdf") and they'll automatically match to the correct equipment!
                    </div>
                  </div>
                  
                  {/* File Dropzone */}
                  <div 
                    className={`file-dropzone ${isDragging ? 'dragging' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      id="bulkFileInput"
                      multiple
                      accept=".pdf,.zip,.jpg,.jpeg,.png"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="bulkFileInput" className="dropzone-label">
                      <div className="dropzone-icon">📁</div>
                      <div className="dropzone-text">
                        <strong>Click to select files</strong> or drag and drop
                      </div>
                      <div className="dropzone-hint">
                        Supports PDF, ZIP, JPG, JPEG, PNG files (Max 100 files)
                      </div>
                    </label>
                  </div>
                  
                  {/* Upload Queue */}
                  {uploadQueue.length > 0 && (
                    <div className="upload-queue">
                      <div className="queue-header">
                        <h4>Upload Queue ({uploadQueue.length} files)</h4>
                        <button className="btn btn-small btn-danger" onClick={clearQueue}>
                          Clear All
                        </button>
                      </div>
                      <div className="queue-list">
                        {uploadQueue.map(item => (
                          <div key={item.id} className="queue-item">
                            <span className="queue-item-name">
                              {item.type === 'zip' && '📦 '}
                              {item.type === 'pdf' && '📄 '}
                              {item.type === 'image' && '🖼️ '}
                              {item.name}
                              {item.type === 'zip' && ` (${item.pdfCount} PDFs)`}
                            </span>
                            <button 
                              className="btn-remove"
                              onClick={() => removeFromQueue(item.id)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Progress Bar */}
                  {processingStatus.isProcessing && (
                    <div className="progress-section">
                      <div className="progress-info">
                        <span>Processing: {processingStatus.currentFile}</span>
                        <span>{processingStatus.processed} / {processingStatus.total}</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${(processingStatus.processed / processingStatus.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="bulk-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={processUploadQueue}
                      disabled={uploadQueue.length === 0 || processingStatus.isProcessing}
                    >
                      {processingStatus.isProcessing ? 'Processing...' : 'Start Processing'}
                    </button>
                  </div>
                </>
              ) : (
                /* Results View */
                <div className="upload-results">
                  <div className="results-summary">
                    <div className="summary-item success">
                      <span className="summary-count">
                        {uploadResults.filter(r => r.status === 'success').length}
                      </span>
                      <span className="summary-label">Successful</span>
                    </div>
                    <div className="summary-item unmatched">
                      <span className="summary-count">
                        {uploadResults.filter(r => r.status === 'unmatched').length}
                      </span>
                      <span className="summary-label">Unmatched</span>
                    </div>
                    <div className="summary-item failed">
                      <span className="summary-count">
                        {uploadResults.filter(r => r.status === 'failed').length}
                      </span>
                      <span className="summary-label">Failed</span>
                    </div>
                  </div>
                  
                  {/* Manual Mapping Section */}
                  {manualMappings.length > 0 && (
                    <div className="manual-mapping-section">
                      <h4>Manual Mapping Required</h4>
                      <p className="mapping-hint">
                        Select equipment for unmatched reports:
                      </p>
                      <div className="mapping-list">
                        {manualMappings.map((item, index) => (
                          <div key={index} className="mapping-item">
                            <span className="mapping-file-name">{item.name}</span>
                            {item.serialInfo && (
                              <span className="mapping-serial">
                                Detected: {item.serialInfo.value}
                              </span>
                            )}
                            <select
                              className="mapping-select"
                              onChange={(e) => handleManualMapping(item.resultIndex, e.target.value)}
                              value=""
                            >
                              <option value="">Select Equipment...</option>
                              {assets.map(asset => (
                                <option key={asset.id} value={asset.id}>
                                  {asset.equipmentName} (S/N: {asset.serialNo || 'N/A'})
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Results List */}
                  <div className="results-list">
                    <h4>Processing Results</h4>
                    {uploadResults.map((result, index) => (
                      <div 
                        key={index} 
                        className={`result-item ${result.status}`}
                      >
                        <span className="result-status">
                          {result.status === 'success' && '✓'}
                          {result.status === 'unmatched' && '?'}
                          {result.status === 'failed' && '✗'}
                        </span>
                        <span className="result-name">{result.name}</span>
                        {result.matchedEquipment && (
                          <span className="result-match">
                            → {result.matchedEquipment.equipmentName}
                          </span>
                        )}
                        {result.error && (
                          <span className="result-error">{result.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="results-actions">
                    <button className="btn btn-secondary" onClick={() => setShowResults(false)}>
                      Back
                    </button>
                    <button 
                      className="btn btn-success"
                      onClick={confirmBulkUpload}
                      disabled={uploadResults.filter(r => r.status === 'success').length === 0}
                    >
                      Confirm Upload ({uploadResults.filter(r => r.status === 'success').length})
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* CAMERA CAPTURE TAB */}
          {activeTab === 'camera' && (
            <div className="camera-tab">
              {/* Report Type Selection */}
              <div className="form-section">
                <label>Report Type:</label>
                <div className="report-type-options">
                  <label className={`radio-label ${reportType === 'pm' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      value="pm"
                      checked={reportType === 'pm'}
                      onChange={(e) => setReportType(e.target.value)}
                    />
                    <span>PM Report</span>
                  </label>
                  <label className={`radio-label ${reportType === 'cal' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      value="cal"
                      checked={reportType === 'cal'}
                      onChange={(e) => setReportType(e.target.value)}
                    />
                    <span>Calibration Report</span>
                  </label>
                  <label className={`radio-label ${reportType === 'both' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      value="both"
                      checked={reportType === 'both'}
                      onChange={(e) => setReportType(e.target.value)}
                    />
                    <span>Both (PM + Calibration)</span>
                  </label>
                </div>
              </div>
              
              {!isCameraActive ? (
                <div className="camera-start">
                  <input
                    ref={cameraFileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    onChange={handleMobileCameraCapture}
                    style={{ display: 'none' }}
                  />
                  <button className="btn btn-primary btn-large" onClick={startCamera}>
                    📷 Start Camera
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ marginTop: '10px' }}
                    onClick={() => cameraFileInputRef.current?.click()}
                  >
                    📱 Capture via Mobile Camera
                  </button>
                  <p className="camera-hint">
                    Capture report pages using your device camera
                  </p>
                </div>
              ) : (
                <div className="camera-active">
                  <div className="camera-preview">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="camera-video"
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                  </div>
                  
                  <div className="camera-controls">
                    <button className="btn btn-success" onClick={capturePhoto}>
                      📸 Capture
                    </button>
                    <button className="btn btn-danger" onClick={stopCamera}>
                      Stop Camera
                    </button>
                  </div>
                </div>
              )}
              
              {/* Captured Images */}
              {capturedImages.length > 0 && (
                <div className="captured-images">
                  <h4>Captured Pages ({capturedImages.length})</h4>
                  <div className="images-grid">
                    {capturedImages.map((img, index) => (
                      <div key={index} className="captured-image-item">
                        <img src={img} alt={`Page ${index + 1}`} />
                        <button 
                          className="btn-remove-image"
                          onClick={() => removeCapturedImage(index)}
                        >
                          ×
                        </button>
                        <span className="page-number">{index + 1}</span>
                      </div>
                    ))}
                  </div>
                  
                  {ocrProgress > 0 && (
                    <div className="ocr-progress">
                      <span>OCR Processing: {ocrProgress}%</span>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${ocrProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <button 
                    className="btn btn-primary"
                    onClick={processCameraImages}
                    disabled={processingStatus.isProcessing}
                  >
                    {processingStatus.isProcessing ? 'Processing...' : 'Convert to PDF & Process'}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* SMART GROUPING TAB */}
          {activeTab === 'grouping' && (
            <div className="grouping-tab">
              {/* Equipment Name Selection - Autocomplete */}
              <EquipmentAutocomplete 
                assets={assets}
                selectedEquipment={selectedEquipment}
                onSelect={(equipmentName) => {
                  setSelectedEquipment(equipmentName);
                  // Auto-select this equipment group
                  if (equipmentName) {
                    setSelectedGroups([equipmentName]);
                  } else {
                    setSelectedGroups([]);
                  }
                }}
              />
              
              {/* Selected Equipment Preview */}
              {selectedEquipment && groupedEquipment[selectedEquipment] && (
                <div className="equipment-preview">
                  <h4>{selectedEquipment}</h4>
                  <p className="equipment-count">
                    {groupedEquipment[selectedEquipment].length} equipment items will receive this report
                  </p>
                  <div className="equipment-list-preview">
                    {groupedEquipment[selectedEquipment].slice(0, 5).map((asset, idx) => (
                      <span key={asset.id} className="equipment-tag">
                        {asset.assetId || asset.serialNo || `Item ${idx + 1}`}
                      </span>
                    ))}
                    {groupedEquipment[selectedEquipment].length > 5 && (
                      <span className="equipment-tag more">
                        +{groupedEquipment[selectedEquipment].length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Report Upload */}
              <div className="form-section">
                <label>Upload Report:</label>
                <div className="report-type-options">
                  <label className={`radio-label ${groupReportType === 'pm' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      value="pm"
                      checked={groupReportType === 'pm'}
                      onChange={(e) => setGroupReportType(e.target.value)}
                    />
                    <span>PM Report</span>
                  </label>
                  <label className={`radio-label ${groupReportType === 'cal' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      value="cal"
                      checked={groupReportType === 'cal'}
                      onChange={(e) => setGroupReportType(e.target.value)}
                    />
                    <span>Calibration Report</span>
                  </label>
                  <label className={`radio-label ${groupReportType === 'both' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      value="both"
                      checked={groupReportType === 'both'}
                      onChange={(e) => setGroupReportType(e.target.value)}
                    />
                    <span>Both (PM + Calibration)</span>
                  </label>
                </div>
                
                <input
                  type="file"
                  id="groupFileInput"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleGroupFileSelect}
                  style={{ display: 'none' }}
                />
                <label htmlFor="groupFileInput" className="btn btn-primary">
                  {groupReportFile ? 'Change File' : 'Choose File'}
                </label>
                {groupReportFile && (
                  <span className="selected-file">{groupReportFile.name}</span>
                )}
              </div>
              
              {/* Execute Button */}
              <button 
                className="btn btn-success btn-large"
                onClick={executeGroupUpload}
                disabled={!selectedEquipment || !groupReportPreview}
              >
                Assign to Selected Equipment
              </button>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default React.memo(BulkUploadModal);
